import { useEffect, useState } from 'react';
import { ExternalLink, DollarSign } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Table } from '@/components/ui/Table';
import { Spinner } from '@/components/ui/Spinner';
import api from '@/lib/api';
import type { ApiResponse, Payment } from '@hirenfire/shared';
import { CHAIN_CONFIGS } from '@hirenfire/shared';

type BadgeVariant = 'warning' | 'success' | 'error' | 'default' | 'primary' | 'purple';

const statusVariant: Record<string, BadgeVariant> = {
  PENDING: 'warning',
  CONFIRMING: 'warning',
  CONFIRMED: 'success',
  FAILED: 'error',
  EXPIRED: 'default',
};

export function PaymentsPage() {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchPayments() {
      try {
        const res = await api.get<ApiResponse<Payment[]>>('/payments');
        if (res.data.data) setPayments(res.data.data);
      } catch {
        // fallback
      } finally {
        setIsLoading(false);
      }
    }
    fetchPayments();
  }, []);

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-white">Payments</h1>
        <p className="text-gray-400 mt-1">View your payment history</p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : payments.length === 0 ? (
        <Card>
          <CardBody className="py-16 text-center">
            <DollarSign className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No payments yet</p>
          </CardBody>
        </Card>
      ) : (
        <Card>
          <Table
            keyExtractor={(p) => p.id}
            columns={[
              {
                key: 'type',
                header: 'Type',
                render: (p) => (
                  <span className="text-white font-medium">
                    {p.type.replace(/_/g, ' ')}
                  </span>
                ),
              },
              {
                key: 'amount',
                header: 'Amount',
                render: (p) => (
                  <span className="font-semibold text-white">
                    ${p.amount} {p.currency}
                  </span>
                ),
              },
              {
                key: 'chain',
                header: 'Chain',
                render: (p) => (
                  <Badge>{CHAIN_CONFIGS[p.chain].name}</Badge>
                ),
              },
              {
                key: 'status',
                header: 'Status',
                render: (p) => (
                  <Badge variant={statusVariant[p.status] ?? 'default'}>
                    {p.status}
                  </Badge>
                ),
              },
              {
                key: 'txHash',
                header: 'TX',
                render: (p) =>
                  p.txHash ? (
                    <a
                      href={`${CHAIN_CONFIGS[p.chain].explorerUrl}/tx/${p.txHash}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary-400 hover:text-primary-300 flex items-center gap-1"
                    >
                      <span className="font-mono text-xs">
                        {p.txHash.slice(0, 8)}...
                      </span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <span className="text-gray-600">--</span>
                  ),
              },
              {
                key: 'createdAt',
                header: 'Date',
                render: (p) => (
                  <span className="text-gray-400">
                    {new Date(p.createdAt).toLocaleDateString()}
                  </span>
                ),
              },
            ]}
            data={payments}
          />
        </Card>
      )}
    </div>
  );
}
